<template>
  <div class="order-entry-flex-panel v-pad">
    <v-form-group v-if="priceTypeVisible" class="c-order-entry">
      <div>
        <label style="display: inline-block">
          {{ i18n('labels.priceType') }}
        </label>
        <div v-if="ticketType === 'fractional'" style="display: inline-block">
          <v-button variant="link" v-b-tooltip.html="getPriceToolTipText" class="pt-0 pb-0" role="tooltip"
            ><font-awesome-icon :icon="['fal', 'question-circle']" /><span class="sr-only">{{
              i18n('labels.priceTypeTooltip')
            }}</span>
          </v-button>
        </div>
      </div>

      <v-form-select
        :aria-label="i18n('labels.priceType')"
        :value="priceType"
        :options="priceTypeOptions"
        :disabled="!priceTypeEnabled || disabled"
        @input="onPriceTypeChanged"
      />
    </v-form-group>
    <div class="order-entry-flex-panel" :style="{ 'flex-direction': limitTriggerDirection }">
      <c-order-entry-price
        :class="{
          'c-order-entry': !isTriggerVerticalPadding,
          'c-order-entry-vertical': isTriggerVerticalPadding
        }"
        :label="triggerLabel"
        :value="trigger"
        :delta="triggerDelta"
        :isDelta="isTriggerDelta"
        :min="priceIncrement"
        :type="triggerType"
        :types="triggerTypes"
        :step="priceIncrement"
        :estimatedPrice="estimatedTriggerPrice"
        :disabled="!triggerEnabled || disabled"
        @value-changed="onTriggerChanged"
        @delta-changed="onTriggerDeltaChanged"
        @type-changed="onTriggerTypeChanged"
        @reset="onTriggerReset"
        v-if="isTriggerVisible || isTriggerDeltaVisible"
        :isModify="isModify"
      />
      <c-order-entry-price
        class="c-order-entry"
        :label="limitLabel"
        :value="limit"
        :delta="limitDelta"
        :isDelta="isLimitDelta"
        :min="ticketType === 'strategy' ? 0 : priceIncrement"
        :type="limitType"
        :types="limitTypes"
        :step="priceIncrement"
        :estimatedPrice="estimatedLimitPrice"
        :disabled="!limitEnabled || disabled"
        :netCreditDebit="netCreditDebit"
        :padding="priceType === 'stopLimit'"
        @value-changed="onLimitChanged"
        @delta-changed="onLimitDeltaChanged"
        @type-changed="onLimitTypeChanged"
        @net-credit-debit-changed="onNetCreditDebitChanged"
        @reset="onLimitReset"
        v-if="isLimitVisible || isLimitDeltaVisible"
        :isModify="isModify"
      />
    </div>
    <div class="order-entry-flex-panel" :style="{ 'flex-direction': durationGoodTillDirection }">
      <v-form-group
        v-if="isDurationVisible"
        :class="{
          'c-order-entry': !isDurationVerticalPadding,
          'c-order-entry-vertical': isDurationVerticalPadding,
          'c-order-entry__duration': true
        }"
      >
        <div>
          <label style="display: inline-block">
            {{ i18n('labels.duration') }}
          </label>
          <div v-if="ticketType === 'fractional'" style="display: inline-block">
            <v-button variant="link" v-b-tooltip.html="getDurationTipText" class="pt-0 pb-0" role="tooltip"
              ><font-awesome-icon :icon="['fal', 'question-circle']" /><span class="sr-only">{{
                i18n('labels.durationTooltip')
              }}</span>
            </v-button>
          </div>
        </div>
        <v-form-select
          v-if="isDurationVisible"
          :value="duration"
          :options="durationOptions"
          :disabled="!durationEnabled || disabled"
          @input="onDurationChanged"
          v-b-tooltip.hover
          :title="durationTooltip"
          :aria-label="i18n('labels.duration')"
        />
      </v-form-group>
      <v-form-group class="c-order-entry" :label="i18n('labels.duration')" v-else>
        <v-form-input
          class="consolidated-duration"
          :value="consolidatedDuration"
          disabled
          :aria-label="i18n('labels.duration')"
        />
      </v-form-group>
      <v-form-group class="c-order-entry" :label="i18n('labels.goodTill')" v-if="isGoodTillVisible">
        <v-form-datepicker
          detached
          boundary="window"
          value-as-date
          label-no-date-selected=""
          :placeholder="i18n('labels.date')"
          :value="goodTill"
          :disabled="!goodTillEnabled"
          :locale="$i18n.locale"
          :labelHelp="$t('white-label.components.datepicker.labelHelp')"
          :labelPrevMonth="$t('white-label.components.datepicker.labelPrevMonth')"
          :labelCurrentMonth="$t('white-label.components.datepicker.labelCurrentMonth')"
          :labelNextMonth="$t('white-label.components.datepicker.labelNextMonth')"
          :date-disabled-fn="disabledDays"
          :date-format-options="{ year: undefined, month: '2-digit', day: '2-digit', weekday: undefined }"
          :min="goodTillStartDate"
          :max="goodTillEndDate"
          @input="onGoodTillChanged"
        />
      </v-form-group>
    </div>
  </div>
</template>

<script>
import COrderEntryPrice from '@custom/COrderEntryPrice';

import moment from 'moment';
import Holidays from '@/utils/tdHolidays';
import { toTimezoneDate } from '@/utils/date';

export default {
  name: 'w-order-entry-price-panel',
  components: {
    COrderEntryPrice
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    leg: {
      type: String,
      required: true
    },
    embedded: {
      type: Boolean,
      default: false
    },
    ticketType: {
      type: String,
      required: true
    },
    priceType: {
      type: String,
      required: true
    },
    priceTypeOptions: {
      type: Array,
      required: true
    },
    priceTypeEnabled: {
      type: Boolean,
      default: false
    },
    priceTypeVisible: {
      type: Boolean,
      default: true
    },
    duration: {
      type: String,
      required: true
    },
    durationOptions: {
      type: Array,
      required: true
    },
    durationEnabled: {
      type: Boolean,
      default: false
    },
    goodTill: {
      required: true
    },
    goodTillEnabled: {
      type: Boolean,
      default: false
    },
    goodTillStart: {
      type: Number | String,
      default: 1
    },
    goodTillEnd: {
      type: Number | String,
      default: 180
    },
    limitEnabled: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number | String,
      default: ''
    },
    limitLabelKey: {
      type: String,
      default: 'limit'
    },
    limitType: {
      type: String,
      default: 'none'
    },
    limitDelta: {
      type: Number | String,
      default: ''
    },
    estimatedLimitPrice: {
      type: String,
      default: ''
    },
    triggerEnabled: {
      type: Boolean,
      default: false
    },
    triggerVisible: {
      type: Boolean,
      default: true
    },
    trigger: {
      type: Number | String,
      default: ''
    },
    triggerType: {
      type: String,
      default: 'none'
    },
    triggerDelta: {
      type: Number | String,
      default: ''
    },
    estimatedTriggerPrice: {
      type: String,
      default: ''
    },
    priceIncrement: {
      type: Number,
      required: true
    },
    profitLossExits: {
      type: Boolean,
      default: false
    },
    netCreditDebit: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateFormat: 'MM-DD-YYYY',
      dateFormatShow: {
        day: true,
        month: true,
        year: false
      }
    };
  },
  computed: {
    consolidatedDuration() {
      if (this.duration === 'gtd') {
        if (this.goodTill) {
          let day = ('0' + this.goodTill.getDate()).slice(-2);
          let month = ('0' + (this.goodTill.getMonth() + 1)).slice(-2);
          return month + '/' + day;
        } else {
          return this.i18n('labels.mm') + '/' + this.i18n('labels.dd');
        }
      } else {
        return this.$t('white-label.globalLabels.trading.duration.options.short.' + this.duration);
      }
    },
    durationTooltip() {
      return this.$t('white-label.globalLabels.trading.duration.options.long.' + this.duration);
    },
    goodTillStartDate() {
      const currDate = toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone);
      let days = currDate.getDay() - 4;
      if (days <= 0) {
        days = 0;
      } else {
        days = 3 - days;
      }

      return moment(currDate)
        .add(days + +this.goodTillStart, 'day')
        .toDate();
    },
    goodTillEndDate() {
      const currDate = toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone);
      return moment(currDate)
        .add(+this.goodTillEnd, 'day')
        .toDate();
    },
    isLimitDeltaVisible() {
      return this.priceType === 'trailingStopLimit';
    },
    isLimitDelta() {
      return (
        this.isLimitDeltaVisible ||
        (this.profitLossExits && (this.limitType.endsWith('$') || this.limitType.endsWith('%')))
      );
    },
    isLimitVisible() {
      return this.priceType === 'limit' || this.priceType === 'stopLimit';
    },
    limitLabel() {
      if (this.isLimitDeltaVisible) {
        return this.i18n('labels.limitDelta');
      } else {
        return this.i18n('labels.' + this.limitLabelKey);
      }
    },
    triggerLabel() {
      if (this.isTriggerDeltaVisible) {
        return this.i18n('labels.triggerDelta');
      } else {
        return this.i18n('labels.trigger');
      }
    },
    limitTriggerDirection() {
      if (
        !this.embedded &&
        !this.profitLossExits &&
        this.ticketType === 'stock' &&
        (this.priceType === 'stopLimit' || this.priceType === 'trailingStopLimit')
      ) {
        return 'column';
      } else {
        return 'row';
      }
    },
    isDurationVisible() {
      return this.leg === 'firstOrder';
    },
    isGoodTillVisible() {
      return this.leg === 'firstOrder' && this.duration === 'gtd';
    },
    durationGoodTillDirection() {
      if (!this.embedded && this.ticketType === 'stock') {
        return 'column';
      } else {
        return 'row';
      }
    },
    isTriggerDeltaVisible() {
      return this.priceType === 'trailingStopLimit' || this.priceType === 'trailingStopMarket';
    },
    isTriggerDelta() {
      return (
        this.isTriggerDeltaVisible ||
        (this.profitLossExits && (this.triggerType.endsWith('$') || this.triggerType.endsWith('%')))
      );
    },
    isTriggerVisible() {
      return this.priceType === 'stopLimit' || this.priceType === 'stopMarket' || this.triggerVisible;
    },
    isTriggerVerticalPadding() {
      return this.durationGoodTillDirection === 'column' && this.priceType === 'stopLimit';
    },
    isDurationVerticalPadding() {
      return this.durationGoodTillDirection === 'column';
    },
    triggerTypes() {
      if (this.isTriggerDeltaVisible) {
        return [
          { name: this.i18n('labels.absOffset'), sr: this.i18n('labels.absOffsetSR'), type: '$', icon: 'dollar-sign' },
          { name: this.i18n('labels.relOffset'), sr: this.i18n('labels.relOffsetSR'), type: '%', icon: 'percent' }
        ];
      } else {
        return this.limitTriggerTypes;
      }
    },
    limitTypes() {
      if (this.isLimitDeltaVisible) {
        return [{ name: this.i18n('labels.absOffset'), type: '$', icon: 'dollar-sign' }];
      } else {
        return this.limitTriggerTypes;
      }
    },
    limitTriggerTypes() {
      if (this.profitLossExits && (this.leg === 'secondOrder' || this.leg === 'thirdOrder')) {
        return [
          { name: this.i18n('labels.none'), type: 'none', icon: 'lock' },
          { name: this.i18n('labels.absOffset'), type: '$', icon: 'dollar-sign' },
          { name: this.i18n('labels.relOffset'), type: '%', icon: 'percent' }
        ];
      } else if (this.ticketType === 'strategy') {
        return [
          { name: this.i18n('labels.none'), type: 'none', icon: 'lock' },
          { name: this.i18n('labels.followNat'), type: 'nat', icon: 'lock' },
          { name: this.i18n('labels.followMid'), type: 'mid', icon: 'lock' }
        ];
      } else {
        return [
          { name: this.i18n('labels.none'), type: 'none', icon: 'lock' },
          { name: this.i18n('labels.followBid'), type: 'bid', icon: 'lock' },
          { name: this.i18n('labels.followAsk'), type: 'ask', icon: 'lock' }
        ];
      }
    },
    isModify() {
      return this.$store.state.widget[this.wid].orderMode.action === 'MODIFY';
    },
    country() {
      const symbol = this.$store.state.widget[this.wid].legs[this.leg].symbol;
      return symbol ? symbol.market : null;
    }
  },
  methods: {
    disabledDays(value, date) {
      let disabled = false;
      if (date.getDay() === 0 || date.getDay() === 6) {
        disabled = true;
      } else {
        if (Holidays.isHoliday(date, this.country)) {
          disabled = true;
        }
      }
      return disabled;
    },
    i18n(subkey) {
      return this.$t(`white-label.${'widgets.orderEntry'}.${subkey}`);
    },
    onDurationChanged(value) {
      if (this.duration !== value) {
        this.$emit('value-changed', { key: 'duration', value: value });
      }
    },
    onGoodTillChanged(value) {
      if (this.goodTill !== value) {
        this.$emit('value-changed', { key: 'goodTill', value: value });
      }
    },
    onLimitChanged(value) {
      if (this.limit !== value) {
        this.$emit('value-changed', { key: 'limit', value: value });
      }
    },
    onLimitDeltaChanged(value) {
      if (this.limitDelta !== value) {
        this.$emit('value-changed', { key: 'limitDelta', value: value });
      }
    },
    onLimitTypeChanged(value) {
      if (this.limitType !== value) {
        this.$emit('value-changed', { key: 'limitType', value: value });
      }
    },
    onLimitReset() {
      this.$emit('value-changed', { key: 'limitType', value: 'none' });
    },
    onNetCreditDebitChanged(data) {
      this.$emit('value-changed', { key: 'netCreditDebit', value: data });
    },
    onPriceTypeChanged(value) {
      if (this.priceType !== value) {
        this.$emit('value-changed', { key: 'priceType', value: value });
      }
    },
    onTriggerChanged(value) {
      if (this.trigger !== value) {
        this.$emit('value-changed', { key: 'trigger', value: value });
      }
    },
    onTriggerDeltaChanged(value) {
      if (this.triggerDelta !== value) {
        this.$emit('value-changed', { key: 'triggerDelta', value: value });
      }
    },
    onTriggerTypeChanged(value) {
      if (this.triggerType !== value) {
        this.$emit('value-changed', { key: 'triggerType', value: value });
      }
    },
    onTriggerReset() {
      this.$emit('value-changed', { key: 'triggerType', value: 'none' });
    },
    getPriceToolTipText() {
      return `<div class="price-tooltip-text">${this.i18n('labels.priceTypeTooltip')}</div>`;
    },
    getDurationTipText() {
      return `<div class="duration-tooltip-text">${this.i18n('labels.durationTooltip')}</div>`;
    }
  },
  watch: {
    durationOptions: {
      immediate: true,
      handler(options) {
        const exist = options.some(opt => opt.value === this.duration);
        if (!exist) {
          //if no valid duration exist, then select the first from the list
          this.onDurationChanged(options[0].value);
        }
      }
    }
  }
};
</script>

<style></style>
